import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import {
  GetCandidateSmartInterviewsVideosOutput,
  smartInterviewsApi,
  SmartInterviewVideoStatus,
  useDeleteCandidateSmartInterviewMutation,
  useGetCandidateSmartInterviewsVideosQuery,
  useUpdateSmartInterviewMutation,
  useUpdateVisibilitySmartInterviewMutation,
} from '$store/modules/smart-interviews';
import { useEffect, useState } from 'react';
import VideoGenericElement from '$components/GenericElements/VideoGenericElement';
import { ReactComponent as Close } from '$icons/uncolored/Close.svg';
import { useTranslation } from 'react-i18next';
import { errorHandler, successHandler } from '$store/utils';
import { FloatLabel } from 'primereact/floatlabel';
import { InputText } from 'primereact/inputtext';
import { useAppDispatch } from '$hooks/use-app-dispatch.hook';
import {
  ErrorMessageEn,
  ErrorMessageEs,
  SuccessMessageEn,
  SuccessMessageEs,
} from '$constants/alerts';
import { userLanguage, ValidLanguages } from '$translations/index';
import { useCandidateId } from '$hooks/useCandidateId';
import SingleSmartInterviewVideo from './SingleSmartInterviewVideo';
import { NoVideos } from '../../NoVideos';

const SmartInterviewsVideosList = () => {
  const [selectedVideo, setSelectedVideo] = useState<
    GetCandidateSmartInterviewsVideosOutput[number] | undefined
  >();
  const id = useCandidateId();
  const [t] = useTranslation('global');
  const dispatch = useAppDispatch();

  const [isOpenVideoModal, setIsOpenVideoModal] = useState(false);
  const [modifyVideoVisibilityModal, setModifyVideoVisibilityModal] = useState<
    { videoId: number; isVisible: boolean } | undefined
  >();
  const [isOpenRenameModal, setIsOpenRenameModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [videoName, setVideoName] = useState('');

  const {
    data: videos,
    isLoading,
    isFetching,
  } = useGetCandidateSmartInterviewsVideosQuery({
    candidateId: id,
    status: SmartInterviewVideoStatus.Completed,
  });
  const [
    updateVisibilitySmartInterview,
    { isLoading: isLoadingUpdateVisibilitySmartInterview },
  ] = useUpdateVisibilitySmartInterviewMutation();
  const [updateSmartInterview, { isLoading: isLoadingUpdateSmartInterview }] =
    useUpdateSmartInterviewMutation();
  const [
    deleteCandidateSmartInterview,
    { isLoading: isLoadingDeleteCandidateSmartInterview },
  ] = useDeleteCandidateSmartInterviewMutation();

  const handleChangeVisibility = async () => {
    await updateVisibilitySmartInterview([
      {
        Id: modifyVideoVisibilityModal?.videoId as number,
        IsVisible: !modifyVideoVisibilityModal?.isVisible as boolean,
      },
    ]).then((resp) => {
      if ('error' in resp) {
        errorHandler(
          '[DeleteCandidateSmartInterview]',
          userLanguage === ValidLanguages.es
            ? ErrorMessageEs.ServerError
            : ErrorMessageEn.ServerError,
          resp,
          true,
          false
        );
      } else {
        successHandler(
          `Video has been successfully ${
            modifyVideoVisibilityModal?.isVisible ? 'hidden' : 'showed'
          }`,
          true,
          false
        );

        setModifyVideoVisibilityModal(undefined);
      }
    });
  };

  const handleRename = async () => {
    await updateSmartInterview({
      smartInterviewId: selectedVideo?.SmartInterviewQuestions?.Id as number,
      disableUpdateVoice: true,
      data: {
        Name: videoName,
      },
    }).then((resp) => {
      if ('error' in resp) {
        errorHandler(
          '[DeleteCandidateSmartInterview]',
          userLanguage === ValidLanguages.es
            ? ErrorMessageEs.ServerError
            : ErrorMessageEn.ServerError,
          resp,
          true,
          false
        );
      } else {
        successHandler('Video has been successfully renamed', true, false);
        setIsOpenRenameModal(false);
        dispatch(
          smartInterviewsApi.util.invalidateTags(['SmartInterviewsVideos'])
        );
      }
    });
  };

  const handleDelete = async () => {
    await deleteCandidateSmartInterview(selectedVideo?.Id as number).then(
      (resp) => {
        if ('error' in resp) {
          errorHandler(
            '[DeleteCandidateSmartInterview]',
            userLanguage === ValidLanguages.es
              ? ErrorMessageEs.ServerError
              : ErrorMessageEn.ServerError,
            resp,
            true,
            false
          );
        } else {
          successHandler(
            userLanguage === ValidLanguages.es
              ? SuccessMessageEs.deleteVideoInternal
              : SuccessMessageEn.deleteVideoInternal,
            true,
            false
          );
        }
      }
    );
  };

  const closeModal = () => {
    setIsOpenVideoModal(false);
    setSelectedVideo(undefined);
  };

  useEffect(() => {
    if (selectedVideo && videos) {
      setSelectedVideo(
        videos.find((singleVideo) => singleVideo.Id === selectedVideo.Id)
      );
    }
  }, [selectedVideo, videos]);

  return (
    <>
      <header className="mt-2 mb-8 text-2xl font-semibold">
        <p className="">{t('AdditionalVideos.smartInterview.title')}</p>
      </header>

      {(!isLoading || !isFetching) &&
        (!videos || (videos && videos.length === 0)) && <NoVideos internal />}
      <div className="grid grid-cols-4 gap-4">
        {videos?.map((singleVideo) => (
          <SingleSmartInterviewVideo
            key={singleVideo.Id}
            video={singleVideo}
            onClick={(video) => {
              setSelectedVideo(video);
              setIsOpenVideoModal(true);
            }}
            openVisibilityModal={(videoVisibility) =>
              setModifyVideoVisibilityModal(videoVisibility)
            }
          />
        ))}
        {/* Video Modal */}
        <ModalInTailwind isOpen={isOpenVideoModal} closeModal={closeModal}>
          <div className="bg-white rounded p-8 flex flex-col w-auto max-w-3xl relative">
            <button
              type="button"
              onClick={closeModal}
              className="absolute top-2 right-2"
            >
              <Close className="stroke-internal-main" />
            </button>
            <div className="flex flex-row justify-between items-start mb-4">
              <h1 className="font-semibold text-xl text-internal-main">
                {selectedVideo?.SmartInterviewQuestions?.Name}
              </h1>
            </div>
            <div className="w-full items-center justify-center flex">
              <VideoGenericElement
                videoData={{
                  Name: selectedVideo?.SmartInterviewQuestions?.Name as string,
                  url: selectedVideo?.Url,
                }}
                controls
                className="max-w-full max-h-2xl"
              />
            </div>
            <div className="flex flex-row justify-between items-center mt-4">
              <button
                type="button"
                className="rounded bg-internal-main px-4 py-2 text-white font-semibold"
                onClick={() => {
                  setIsOpenRenameModal(true);
                  setVideoName(
                    selectedVideo?.SmartInterviewQuestions?.Name || ''
                  );
                }}
              >
                {t('AdditionalVideos.modals.confirmUpdate')}
                <i className="pi pi-pencil ml-2" />
              </button>
              <button
                type="button"
                className="bg-internal-main py-2 px-4 text-white font-semibold rounded"
                onClick={() => setIsOpenDeleteModal(true)}
              >
                {t('AdditionalVideos.buttons.delete')}
                <i className="pi pi-trash ml-2" />
              </button>
            </div>
          </div>
        </ModalInTailwind>

        {/* Visibility Modal */}
        <ModalInTailwind
          isOpen={!!modifyVideoVisibilityModal}
          closeModal={() => setModifyVideoVisibilityModal(undefined)}
        >
          <div className="bg-white rounded p-8 w-1/3">
            <h1 className="text-xl font-semibold">
              {modifyVideoVisibilityModal?.isVisible
                ? t('AdditionalVideos.modals.hide')
                : t('AdditionalVideos.modals.show')}
            </h1>
            <hr className="mb-4" />
            <p>
              {modifyVideoVisibilityModal?.isVisible
                ? t('AdditionalVideos.modals.hideQuestion1')
                : t('AdditionalVideos.modals.showQuestion1')}
            </p>
            <div className="flex flex-row gap-8 justify-end mt-8">
              <fieldset disabled={isLoadingUpdateVisibilitySmartInterview}>
                <button
                  type="button"
                  className="rounded text-internal-main px-4 py-2 font-semibold"
                  onClick={() => setModifyVideoVisibilityModal(undefined)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="bg-internal-main py-2 px-4 text-white font-semibold rounded"
                  onClick={handleChangeVisibility}
                >
                  {modifyVideoVisibilityModal?.isVisible
                    ? t('AdditionalVideos.modals.hide')
                    : t('AdditionalVideos.modals.show')}
                  <i className="pi pi-arrow-right ml-2" />
                </button>
              </fieldset>
            </div>
          </div>
        </ModalInTailwind>

        {/* Rename Modal */}
        <ModalInTailwind
          isOpen={isOpenRenameModal}
          closeModal={() => setIsOpenRenameModal(false)}
        >
          <div className="bg-white rounded p-8 w-full max-w-2xl">
            <h1 className="text-xl font-semibold">
              {t('AdditionalVideos.modals.update')}
            </h1>
            <hr className="mb-8 mt-2" />
            <FloatLabel className="w-full">
              <InputText
                className="w-full"
                id="videoName"
                value={videoName}
                onChange={(e) => setVideoName(e.target.value)}
              />
              <label htmlFor="username">
                {t('AdditionalVideos.modals.update')}
              </label>
            </FloatLabel>
            <div className="flex justify-end">
              <fieldset
                className="flex flex-row gap-8 mt-8"
                disabled={isLoadingUpdateSmartInterview}
              >
                <button
                  type="button"
                  className="rounded text-internal-main px-4 py-2 font-semibold"
                  onClick={() => setIsOpenRenameModal(false)}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="bg-internal-main py-2 px-4 text-white font-semibold rounded"
                  onClick={handleRename}
                >
                  {t('AdditionalVideos.modals.confirmUpdate')}
                  <i className="pi pi-arrow-right ml-2" />
                </button>
              </fieldset>
            </div>
          </div>
        </ModalInTailwind>

        {/* Delete Modal */}
        <ModalInTailwind
          isOpen={isOpenDeleteModal}
          closeModal={() => setIsOpenDeleteModal(false)}
        >
          <div className="bg-white rounded p-8">
            <h1 className="text-xl font-semibold">
              {t('AdditionalVideos.modals.delete')}
            </h1>
            <hr className="mb-8 mt-2" />
            <p>{t('AdditionalVideos.modals.question')}</p>
            <div className="mt-8">
              <fieldset
                className="flex justify-end gap-4"
                disabled={isLoadingDeleteCandidateSmartInterview}
              >
                <button
                  type="button"
                  className="rounded text-internal-main px-4 py-2 font-semibold"
                  onClick={() => setIsOpenDeleteModal(false)}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="bg-internal-main py-2 px-4 text-white font-semibold rounded"
                  onClick={handleDelete}
                >
                  {t('AdditionalVideos.buttons.delete')}
                  <i className="pi pi-arrow-right ml-2" />
                </button>
              </fieldset>
            </div>
          </div>
        </ModalInTailwind>
      </div>
    </>
  );
};

export default SmartInterviewsVideosList;
